import { defineStore } from "pinia";
import { ItemType, PendingRegistrationsApi, SessionsApi } from "@simbelapp/order-sdk";
import type { ProjectDetails } from "@simbelapp/order-sdk";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { useSessionsStore } from "~/store/sessions/sessions-store";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { DateChoiceOutcomeEnum, DateChoiceStatusEnum } from "~/utils/enums/date-choice.enums";
import { dateChoiceErrorMessage, dateChoiceRequestError } from "~/utils/helpers/date-choice/date-choice-error-message";
import { toAttendeeCard, toEligibleSession } from "~/utils/helpers/date-choice/session-cards";
import type {
  IDateChoiceProposeContext,
  IDateChoiceRemovePayload,
  IDateChoiceStore,
  IDateChoiceTarget,
} from "~/utils/interfaces/date-choice-interfaces";

/** Automatic reminder milestones shown to the admin (US1.4). */
export const DATE_CHOICE_REMINDER_DAYS = [7, 14, 21];

/**
 * Prefix of rows carried by a project. Careful: it is shared by **every** project row (drafts
 * included), so it is not a pending-registration marker — only the row status or the presence of
 * the `dateChoice` block are.
 */
export const PROJECT_ROW_ID_PREFIX = "pr-";

/** `pr-<projectUserId>` → `<projectUserId>`, the key of every `pending-registrations` endpoint. */
export function toProjectUserId(trainingPlanId: string | null | undefined): string | null {
  if (!trainingPlanId?.startsWith(PROJECT_ROW_ID_PREFIX)) return null;
  return trainingPlanId.slice(PROJECT_ROW_ID_PREFIX.length) || null;
}

/** The 2 statuses of the "À planifier" tab — the only reliable discriminant on the list side. */
const PENDING_STATUSES: string[] = [DateChoiceStatusEnum.PENDING_DATE_CHOICE, DateChoiceStatusEnum.NO_SUITABLE_DATE];

export function isPendingRegistrationRow(status: string | null | undefined): boolean {
  return !!status && PENDING_STATUSES.includes(status);
}

/**
 * The catalog's session picker — the single list both date-choice surfaces read, so an admin, a
 * participant and the catalog are never offered different dates for the same training.
 */
async function fetchTrainingSessions(trainingId: string): Promise<ProjectDetails[]> {
  const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionsApi);
  return await apiInstance.findAllTrainingAvailableSessions({
    trainingId,
    // Sessions only: a draft project has no session id to file the registration on.
    withProjects: "false",
    // A session already started, but not over, stays choosable.
    inProgress: "true",
  });
}

export const useDateChoiceStore = defineStore("dateChoiceStore", {
  state: (): IDateChoiceStore => ({
    eligibleSessions: [],
    funnelSessionsCount: 0,
    funnelUpcomingSessionsCount: 0,
    funnelSessionsRequestToken: 0,
    isFunnelSessionsLoading: false,
    hasFunnelSessionsAnswer: false,
    choiceOptions: null,
    projectUserId: null,
    proposeContext: null,
    actionTargets: [],
    comment: "",
    funnelSelection: null,
    funnelChoice: null,
    isLoading: false,
    isSending: false,
    showProposeModal: false,
    showSentModal: false,
    showAvailableSessionsModal: false,
    showEditPriceModal: false,
    showRemoveModal: false,
    showNoSuitableDateModal: false,
    showParticipantModal: false,
  }),
  getters: {
    /**
     * Sessions the admin can pick on the participant's behalf. A full one stays listed (AC-CDD.12)
     * and is pickable only when the session itself allows overbooking — same rule as the
     * participant's card, so the two screens never disagree.
     */
    selectableEligibleSessions(state) {
      return state.eligibleSessions.filter((session) => session.isSelectable);
    },
    /** Participant cards, as returned by `choice-options` (never any price). */
    choiceSessions(state) {
      return state.choiceOptions?.sessions ?? [];
    },
    /** Bulk actions target a session: a multi-training selection has no target. */
    targetTrainingId(state): string {
      return state.actionTargets[0]?.trainingId ?? "";
    },
    targetProjectUserIds(state): string[] {
      return state.actionTargets.map((target) => target.projectUserId);
    },
  },
  actions: {
    /**
     * Sessions of the training, sorted by start date (AC-CDD.9) — the list the catalog already
     * offers when registering to a training, so both surfaces propose the same dates.
     * `pending-registrations/eligible-sessions` used to serve this screen, but it only keeps
     * sessions flagged `is_visible_in_tp`: an inter session nobody has booked yet is not in the
     * training plan, and the admin was offered a strict subset of the catalog dates.
     */
    async fetchEligibleSessions(trainingId: string): Promise<void> {
      const feedback = useFeedback();
      this.isLoading = true;
      try {
        this.eligibleSessions = (await fetchTrainingSessions(trainingId)).map(toEligibleSession);
      } catch (error) {
        this.eligibleSessions = [];
        feedback.error(await dateChoiceRequestError(error), "small");
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * "X session(s) disponible(s)" tag of the "Que voulez-vous faire ?" choice. Drafts count too
     * (AC-CDD.3), hence `withProject: "true"`: the same endpoint the session list reads, plus the
     * draft projects that list deliberately leaves out.
     * The same answer yields the sessions a date choice could actually be delegated on: with
     * `with_projects=true` the back stamps the drafts `status=project` and concatenates them onto
     * the very list `with_projects=false` serves alone — the one `fetchEligibleSessions` reads. One
     * call, so the funnel and the sending screen can never disagree on "aucune session à venir".
     * A rejected request proves nothing: the counters stay at 0 but `hasFunnelSessionsAnswer` does
     * not, so the funnel reports the failure instead of closing the delegation on it.
     */
    async fetchFunnelSessionsCount(trainingId: string | null): Promise<void> {
      // Identifies THIS request: the training id alone cannot tell two calls on the same training
      // apart, so a selection switched away and back would let the older answer win.
      const requestToken = ++this.funnelSessionsRequestToken;
      this.funnelSessionsCount = 0;
      this.funnelUpcomingSessionsCount = 0;
      this.hasFunnelSessionsAnswer = false;
      if (!trainingId) {
        // Nothing in flight for the current selection: a raised flag would hold the choice disabled
        // for good, since the answer of the request left pending can no longer clear it.
        this.isFunnelSessionsLoading = false;
        return;
      }

      const feedback = useFeedback();
      const sessionsStore = useSessionsStore();
      this.isFunnelSessionsLoading = true;
      try {
        const rows: ProjectDetails[] = await sessionsStore.fetchSessionsByTrainingId({
          trainingId,
          withProject: "true",
          inProgress: "true",
          // Without it the store answers `[]` on a failure, which reads exactly like "no session".
          throwOnError: true,
        });
        // A slower earlier request must not overwrite the count of the selection now current.
        if (this.funnelSessionsRequestToken !== requestToken) return;
        this.funnelSessionsCount = rows.length;
        this.funnelUpcomingSessionsCount = rows.filter((row) => row.status !== ItemType.Project).length;
        this.hasFunnelSessionsAnswer = true;
      } catch (error) {
        // Same guard, read after the parse: reading the error body yields, so a newer selection may
        // have superseded this request in between, and its failure is none of that selection's business.
        const message = await dateChoiceRequestError(error);
        if (this.funnelSessionsRequestToken === requestToken) feedback.error(message, "small");
      } finally {
        // Same guard: a stale answer must not clear the loading state of the request now current.
        if (this.funnelSessionsRequestToken === requestToken) this.isFunnelSessionsLoading = false;
      }
    },

    /**
     * Sends the date choice request: one pending registration per participant, in a single
     * transaction (AC-CDD.29). Nothing is created if the sending fails (AC-CDD.23).
     */
    async inviteToChooseDate(): Promise<boolean> {
      // A batch of 40+ attendees takes about a minute: a second click must not queue a second batch.
      if (!this.proposeContext || this.isSending) return false;

      const feedback = useFeedback();
      // Raised before the first await, so the button is already disabled when the click handler returns.
      this.isSending = true;
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, PendingRegistrationsApi);
        const response = await apiInstance.inviteToChooseDate({
          inviteToChooseDateDto: {
            trainingId: this.proposeContext.trainingId,
            attendees: this.proposeContext.participants
              .filter((participant) => !!participant.userId)
              .map((participant) => ({
                userId: participant.userId as string,
                requestId: participant.requestId ?? undefined,
                previousSessionId: participant.previousSessionId ?? undefined,
              })),
            comment: this.comment || undefined,
          },
        });

        // A participant flagged "do not notify" gets no email, hence no way to choose their date.
        const skipped = response?.emailsSkippedForDoNotNotify ?? [];
        if (skipped.length) {
          feedback.info(
            `${skipped.length} participant(s) n'ont pas reçu l'e-mail (option « ne pas notifier ») : ${skipped.join(", ")}`,
            "small",
          );
        }

        this.showProposeModal = false;
        this.showSentModal = true;
        return true;
      } catch (error) {
        feedback.error(await dateChoiceRequestError(error), "small");
        return false;
      } finally {
        this.isSending = false;
      }
    },

    /**
     * Registers the targeted participants on their behalf, without an invitation email. Rows are
     * independent: the number actually processed is reported back to the admin (AC-SUIVI.15).
     */
    async adminChooseSession(sessionId: string): Promise<boolean> {
      const projectUserIds = this.targetProjectUserIds;
      if (!projectUserIds.length) return false;

      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, PendingRegistrationsApi);
      const feedback = useFeedback();
      try {
        const response = await apiInstance.adminChoosePendingRegistrationsSession({
          adminChooseSessionDto: { projectUserIds, sessionId },
        });

        const skipped = (response?.results ?? []).filter((row) => row.outcome === DateChoiceOutcomeEnum.SKIPPED);
        if (skipped.length) {
          feedback.error(
            `${response.processedCount} inscription(s) confirmée(s), ${skipped.length} ignorée(s) : ${dateChoiceErrorMessage(skipped[0]?.reason)}`,
            "small",
          );
        } else {
          feedback.success(
            response.processedCount > 1
              ? `${response.processedCount} inscriptions confirmées`
              : "Inscription confirmée",
            "small",
          );
        }
        return response.processedCount > 0;
      } catch (error) {
        feedback.error(await dateChoiceRequestError(error), "small");
        return false;
      }
    },

    /**
     * Records « aucune date ne convient » on the participants' behalf, from the admin's session
     * choice screen (AC-SUIVI.17). The endpoint is per-registration, so a bulk selection is
     * signalled row by row and the number actually processed is reported back, like a bulk
     * registration.
     * Its participant twin (`signalNoSuitableDate`) resolves the row from the token holder, so an
     * admin always got a 404 there: the admin scope lives on `admin-signal-no-suitable-date` only.
     */
    async adminSignalNoSuitableDate(): Promise<boolean> {
      const projectUserIds = this.targetProjectUserIds;
      if (!projectUserIds.length) return false;

      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, PendingRegistrationsApi);
      const feedback = useFeedback();
      const outcomes = await Promise.allSettled(
        projectUserIds.map((projectUserId) => apiInstance.adminSignalNoSuitableDate({ projectUserId })),
      );

      const rejected = outcomes.filter((outcome) => outcome.status === "rejected") as PromiseRejectedResult[];
      const signalled = outcomes.length - rejected.length;

      // Read once: `reason` holds the rejected call, and its body can only be consumed once.
      const failure = rejected.length ? await dateChoiceRequestError(rejected[0]?.reason) : "";

      if (!signalled) {
        feedback.error(failure, "small");
        return false;
      }
      if (rejected.length) {
        feedback.error(
          `${signalled} inscription(s) mise(s) à jour, ${rejected.length} ignorée(s) : ${failure}`,
          "small",
        );
      } else {
        feedback.success(signalled > 1 ? `${signalled} inscriptions mises à jour` : "Signalement enregistré", "small");
      }
      return true;
    },

    /** Forecast price excl. tax — only on rows that are still pending. */
    async updatePendingRegistrationPrice(projectUserId: string, priceExclTax: number): Promise<boolean> {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, PendingRegistrationsApi);
      const feedback = useFeedback();
      try {
        await apiInstance.updatePendingRegistrationPrice({
          projectUserId,
          updatePendingRegistrationPriceDto: { priceExclTax },
        });
        feedback.success("Prix mis à jour", "small");
        return true;
      } catch (error) {
        feedback.error(await dateChoiceRequestError(error), "small");
        return false;
      }
    },

    /** Deletes the invitation: carrier cascade, reminders cancelled, budget released (AC-CDD.37). */
    async removePendingRegistration(projectUserId: string, payload: IDateChoiceRemovePayload = {}): Promise<boolean> {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, PendingRegistrationsApi);
      const feedback = useFeedback();
      try {
        await apiInstance.removePendingRegistration({
          projectUserId,
          removePendingRegistrationDto: {
            requestAction: payload.requestAction ?? undefined,
            requestComment: payload.requestComment ?? undefined,
          },
        });
        feedback.success("Inscription supprimée", "small");
        return true;
      } catch (error) {
        feedback.error(await dateChoiceRequestError(error), "small");
        return false;
      }
    },

    /**
     * Feed of the participant screen. A 404 means "not the recipient" (fail-closed): the page
     * renders its own state, without an error toast.
     */
    async fetchChoiceOptions(projectUserId: string): Promise<boolean> {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, PendingRegistrationsApi);
      this.isLoading = true;
      this.projectUserId = projectUserId;
      try {
        this.choiceOptions = await apiInstance.getPendingRegistrationChoiceOptions({ projectUserId });
      } catch {
        this.choiceOptions = null;
        this.isLoading = false;
        return false;
      }

      // `choice-options` serves the same `is_visible_in_tp` subset as the admin endpoint, so the
      // cards are re-read from the catalog list. A failure here keeps the back's own list: the link
      // is valid, only the sessions could not be refreshed — not a reason to fail the page closed.
      try {
        const sessions = await fetchTrainingSessions(this.choiceOptions.trainingId);
        this.choiceOptions.sessions = sessions.map(toAttendeeCard);
      } catch {
        /* the back's list stays on screen */
      } finally {
        this.isLoading = false;
      }
      return true;
    },

    /** The participant validates their session: the row becomes an "Inscrit" registration. */
    async chooseSession(sessionId: string): Promise<{ bookingId: string; sessionId: string } | null> {
      if (!this.projectUserId) return null;

      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, PendingRegistrationsApi);
      const feedback = useFeedback();
      try {
        return await apiInstance.choosePendingRegistrationSession({
          projectUserId: this.projectUserId,
          chooseSessionDto: { sessionId },
        });
      } catch (error) {
        feedback.error(await dateChoiceRequestError(error), "small");
        // The list may have shifted under the participant's feet (AC-CHOIX.17).
        await this.fetchChoiceOptions(this.projectUserId);
        return null;
      }
    },

    /** "Aucune date ne me convient": timestamps and stops the reminders, nothing else (AC-NODATE.5). */
    async signalNoSuitableDate(): Promise<boolean> {
      if (!this.projectUserId) return false;

      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, PendingRegistrationsApi);
      const feedback = useFeedback();
      try {
        await apiInstance.signalNoSuitableDate({ projectUserId: this.projectUserId });
        // The CTA stays open: the participant can come back and choose later (AC-NODATE.11).
        await this.fetchChoiceOptions(this.projectUserId);
        return true;
      } catch (error) {
        feedback.error(await dateChoiceRequestError(error), "small");
        return false;
      }
    },

    /**
     * "Proposer un choix de date" is mounted only once per page: the calling flow provides the
     * training and the targeted recipients.
     */
    openProposeModal(context: IDateChoiceProposeContext) {
      this.comment = "";
      this.proposeContext = context;
      this.showProposeModal = true;
    },

    /** Opens the "Sessions disponibles" modal on a single row or a bulk selection. */
    openAvailableSessionsModal(targets: IDateChoiceTarget[]) {
      this.actionTargets = targets;
      this.showAvailableSessionsModal = true;
    },

    /**
     * Resets the "Session" step back to the "Que voulez-vous faire ?" choice.
     * Called when opening the funnel so a previous choice doesn't leak into it.
     */
    resetFunnel() {
      this.funnelSelection = null;
      this.funnelChoice = null;
      this.proposeContext = null;
    },

    closeAllModals() {
      this.showProposeModal = false;
      this.showSentModal = false;
      this.showAvailableSessionsModal = false;
      this.showEditPriceModal = false;
      this.showRemoveModal = false;
      this.showNoSuitableDateModal = false;
      this.showParticipantModal = false;
      this.actionTargets = [];
    },
  },
});
